import { getLogger } from 'src/utils/datadog-logger';

const NONCE_STORAGE_KEY = 'dutchie:oauthFlowNonces';
const NONCE_TTL_MS = 5 * 60 * 1000;
const MAX_OUTSTANDING_NONCES = 5;

export type OAuthNonceOutcome = 'expired' | 'match' | 'mismatch' | 'missing';
export type OAuthProvider = 'apple' | 'google' | 'unknown';

type Nonces = Record<string, number>;

const readNonces = (): Nonces => {
  const stored = window.localStorage.getItem(NONCE_STORAGE_KEY);
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored ?? '{}');
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(parsed).filter(
      ([nonce, timestamp]) =>
        /^[A-Za-z0-9_-]{16,64}$/.test(nonce) && typeof timestamp === 'number' && Number.isFinite(timestamp)
    )
  );
};

export const createOAuthFlowNonce = (): string | null => {
  try {
    const bytes = window.crypto.getRandomValues(new Uint8Array(24));
    const nonce = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    const now = Date.now();
    const unexpired = Object.entries(readNonces())
      .filter(([, createdAt]) => createdAt <= now && now - createdAt <= NONCE_TTL_MS)
      .sort(([, first], [, second]) => first - second)
      .slice(-(MAX_OUTSTANDING_NONCES - 1));

    window.localStorage.setItem(NONCE_STORAGE_KEY, JSON.stringify(Object.fromEntries([...unexpired, [nonce, now]])));
    return nonce;
  } catch {
    return null;
  }
};

export const consumeOAuthFlowNonce = (nonce: string[] | string | null | undefined): OAuthNonceOutcome => {
  if (typeof nonce !== 'string' || !nonce) {
    return 'missing';
  }

  try {
    const nonces = readNonces();
    if (!Object.prototype.hasOwnProperty.call(nonces, nonce)) {
      return 'mismatch';
    }
    const createdAt = nonces[nonce];
    if (createdAt > Date.now() || Date.now() - createdAt > NONCE_TTL_MS) {
      return 'expired';
    }

    const remaining = Object.fromEntries(Object.entries(nonces).filter(([candidate]) => candidate !== nonce));
    window.localStorage.setItem(NONCE_STORAGE_KEY, JSON.stringify(remaining));
    return 'match';
  } catch {
    return 'mismatch';
  }
};

export const stripOAuthCallbackTokens = (): void => {
  const callbackUrl = new URL(window.location.href);
  callbackUrl.searchParams.delete('token');
  callbackUrl.searchParams.delete('oauth_nonce');
  window.history.replaceState(
    window.history.state,
    '',
    `${callbackUrl.pathname}${callbackUrl.search}${callbackUrl.hash}`
  );
};

const logger = getLogger('oauth-nonce');

export const reportOAuthNonceOutcome = (outcome: OAuthNonceOutcome, provider: OAuthProvider, route: string): void => {
  logger.info('OAuth callback nonce outcome', { outcome, oauth_provider: provider, route });
};

export const reportEmbeddedOAuthSender = (sameOrigin: boolean, isPopupSource: boolean): void => {
  logger.info('Embedded OAuth sender', { same_origin: sameOrigin, is_popup_source: isPopupSource });
};
